/* Shared custom interactions. Original theme scripts remain unchanged. */
(() => {
  if (customElements.get('kim-header-search')) return;

  class KimHeaderSearch extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.header-kim-section');
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.panel = this.section.querySelector('.thb-quick-search');
      this.results = this.panel.querySelector('.thb-quick-search--results');
      this.predictive = this.dataset.predictive === 'true' && !!this.results;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };

      this.form.addEventListener('submit', (event) => {
        if (!this.input.value.trim()) event.preventDefault();
      }, options);
      this.input.addEventListener('input', () => {
        this.cancelRequest();
        if (!this.input.value.trim()) {
          this.close();
          return;
        }
        if (this.predictive) this.timer = setTimeout(() => this.search(), 300);
      }, options);
      this.input.addEventListener('focus', () => {
        if (this.predictive && this.input.value.trim()) this.search();
      }, options);
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown' || !this.section.classList.contains('search-open')) return;
        const first = this.results?.querySelector('.search-results.active a');
        if (first) {
          event.preventDefault();
          first.focus();
        }
      }, options);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          const wasInPanel = this.panel.contains(document.activeElement);
          this.close();
          if (wasInPanel) this.input.focus();
          // Focus can initiate a request; Escape must leave the panel closed.
          this.close();
        }
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target) && !this.panel.contains(event.target)) this.close();
      }, options);
      this.section.addEventListener('focusout', () => {
        queueMicrotask(() => {
          if (!this.contains(document.activeElement) && !this.panel.contains(document.activeElement)) this.close();
        });
      }, options);
      this.section.querySelector('.mobile-toggle')?.addEventListener('click', () => this.close(), options);
      this.results?.addEventListener('click', (event) => {
        const tab = event.target.closest('.search-categories a');
        if (!tab) return;
        const target = Array.from(this.results.querySelectorAll('.search-results')).find((item) => `#${item.id}` === tab.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        this.results.querySelectorAll('.search-categories a, .search-results').forEach((item) => item.classList.remove('active'));
        tab.classList.add('active');
        target.classList.add('active');
      }, options);
      // Keep the theme's height variables correct when a breakpoint changes the layout.
      this.resizeObserver = new ResizeObserver(() => window.dispatchEvent(new Event('scroll')));
      this.resizeObserver.observe(this.section.querySelector('theme-header'));
    }

    cancelRequest() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.results?.classList.remove('loading');
    }

    close() {
      this.cancelRequest();
      this.section.classList.remove('search-open');
      this.input.setAttribute('aria-expanded', 'false');
    }

    async search() {
      this.cancelRequest();
      const query = this.input.value.trim();
      if (!query || !this.predictive) return;
      const controller = new AbortController();
      this.request = controller;
      this.section.classList.add('search-open');
      this.input.setAttribute('aria-expanded', 'true');
      this.results.classList.remove('active');
      this.results.classList.add('loading');
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,article,query,page',
        'resources[limit]': '10',
        'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku',
        section_id: 'predictive-search'
      });
      try {
        const response = await fetch(`${window.theme.routes.predictive_search_url}?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error(`Search returned ${response.status}`);
        const markup = await response.text();
        if (controller.signal.aborted || this.input.value.trim() !== query) return;
        const content = new DOMParser().parseFromString(markup, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!content) throw new Error('Search results unavailable');
        this.results.innerHTML = content.innerHTML;
        this.results.querySelectorAll('a[tabindex="-1"]').forEach((link) => link.removeAttribute('tabindex'));
        this.results.classList.add('active');
      } catch (error) {
        if (error.name !== 'AbortError' && this.request === controller) this.close();
      } finally {
        if (this.request === controller) this.results.classList.remove('loading');
      }
    }

    disconnectedCallback() {
      this.cancelRequest();
      this.listeners?.abort();
      this.resizeObserver?.disconnect();
    }
  }
  customElements.define('kim-header-search', KimHeaderSearch);
})();

/* Mega menu adapter: delegate panel transitions to Habitat's TabbedContent. */
(() => {
  if (customElements.get('kim-mega-menu')) return;

  class KimMegaMenu extends HTMLElement {
    connectedCallback() {
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      customElements.whenDefined('tabbed-content').then(() => {
        if (!this.isConnected || signal.aborted) return;
        this.tabs = this.querySelector('tabbed-content');
        this.buttons = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.item = this.closest('.menu-item-has-megamenu');
        this.opener = this.item.querySelector(':scope > a');
        this.addEventListener('pointerover', (event) => {
          if (event.pointerType === 'touch') return;
          const button = event.target.closest('[role="tab"]');
          if (this.buttons.includes(button)) this.select(this.buttons.indexOf(button));
        }, { signal });
        this.addEventListener('focusin', (event) => {
          const button = event.target.closest('[role="tab"]');
          if (this.buttons.includes(button)) this.select(this.buttons.indexOf(button));
        }, { signal });
        // Capture clicks so the native click handler cannot interrupt an in-flight fade.
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[role="tab"]');
          if (!this.buttons.includes(button)) return;
          event.preventDefault();
          event.stopPropagation();
          this.select(this.buttons.indexOf(button));
        }, { capture: true, signal });
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.item.classList.add('kim-mega-dismissed');
            this.opener.focus();
            return;
          }
          const index = this.buttons.indexOf(event.target);
          if (index < 0) return;
          let next;
          if (event.key === 'ArrowDown') next = (index + 1) % this.buttons.length;
          if (event.key === 'ArrowUp') next = (index - 1 + this.buttons.length) % this.buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.buttons.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.buttons[next].focus();
          this.select(next);
        }, { signal });
        this.item.addEventListener('pointerenter', () => this.item.classList.remove('kim-mega-dismissed'), { signal });
        this.item.addEventListener('focusin', (event) => {
          if (!this.item.contains(event.relatedTarget)) this.item.classList.remove('kim-mega-dismissed');
        }, { signal });
        this.observer = new MutationObserver(() => this.syncAccessibility());
        this.observer.observe(this.tabs, { attributes: true, attributeFilter: ['selected-index'] });
        this.syncAccessibility();
      });
    }

    select(index) {
      if (index < 0 || index >= this.buttons.length) return;
      this.pendingIndex = index;
      if (this.frame) return;
      const apply = () => {
        this.frame = null;
        if (!this.isConnected) return;
        // Native TabbedContent expects its previous transition to finish first.
        // Keep only the latest requested category during rapid hover/focus changes.
        if (this.tabs.tl && this.tabs.tl.isActive()) {
          this.frame = requestAnimationFrame(apply);
          return;
        }
        if (this.tabs.selectedIndex !== this.pendingIndex) this.tabs.selectedIndex = this.pendingIndex;
        this.syncAccessibility();
      };
      apply();
    }

    syncAccessibility() {
      const index = this.tabs.selectedIndex;
      this.buttons.forEach((button, i) => {
        button.setAttribute('aria-selected', String(i === index));
        button.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => {
        panel.setAttribute('aria-hidden', String(i !== index));
        // Prevent keyboard focus entering the panel while the old content fades out.
        panel.inert = i !== index;
      });
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.item?.classList.remove('kim-mega-dismissed');
    }
  }
  customElements.define('kim-mega-menu', KimMegaMenu);
})();

/* Kim Custom collection showcase: theme Flickity on desktop; native grid on mobile. */
(() => {
  if (customElements.get('kim-collection-showcase')) return;

  class KimCollectionShowcase extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.kim-showcase__products');
      this.mobileExtraProducts = Array.from(this.track.querySelectorAll('[data-desktop-visible="false"]'));
      this.controls = this.querySelector('.kim-showcase__controls');
      this.previousButton = this.querySelector('.kim-showcase__previous');
      this.nextButton = this.querySelector('.kim-showcase__next');
      this.progress = this.querySelector('.kim-showcase__progress');
      this.desktop = window.matchMedia('(min-width: 1068px)');
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.onBreakpoint = () => this.updateMode();
      this.desktop.addEventListener('change', this.onBreakpoint);
      this.previousButton.addEventListener('click', () => this.carousel?.previous(), options);
      this.nextButton.addEventListener('click', () => this.carousel?.next(), options);
      this.addEventListener('lazyloaded', () => this.scheduleResize(), options);
      this.addEventListener('load', () => this.scheduleResize(), { ...options, capture: true });
      this.resizeObserver = new ResizeObserver(() => this.scheduleResize());
      this.resizeObserver.observe(this.track);
      this.updateMode();
    }

    updateMode() {
      if (!this.desktop.matches) {
        this.destroyCarousel();
        return;
      }
      if (this.carousel || typeof window.Flickity !== 'function') return;
      if (!this.track.querySelector('[data-desktop-visible="true"]')) return;
      // Only desktop-limit cells enter the carousel, even when mobile requests more.
      this.carousel = new window.Flickity(this.track, {
        cellSelector: '.kim-showcase__product[data-desktop-visible="true"]',
        cellAlign: document.dir === 'rtl' ? 'right' : 'left',
        rightToLeft: document.dir === 'rtl',
        contain: true,
        groupCells: true,
        wrapAround: false,
        autoPlay: false,
        pageDots: false,
        prevNextButtons: false,
        accessibility: true
      });
      this.onCarouselChange = () => this.updateControls();
      this.carousel.on('select', this.onCarouselChange);
      this.carousel.on('resize', this.onCarouselChange);
      this.equalizeCards();
      this.updateControls();
    }

    updateControls() {
      if (!this.carousel) return;
      const count = this.carousel.slides.length;
      const index = this.carousel.selectedIndex;
      const progress = count ? Math.round(((index + 1) / count) * 100) : 0;
      this.controls.hidden = count <= 1;
      this.previousButton.disabled = index <= 0;
      this.nextButton.disabled = index >= count - 1;
      this.progress.setAttribute('aria-valuenow', String(progress));
      this.progress.firstElementChild.style.width = `${progress}%`;
    }

    equalizeCards() {
      if (!this.carousel) return;
      // Measure every desktop product, including off-screen pages, at natural
      // height so shorter text and narrower/wider screens can shrink as well.
      this.classList.add('kim-showcase--measuring');
      let height = 0;
      try {
        this.carousel.cells.forEach(cell => {
          height = Math.max(height, cell.element.getBoundingClientRect().height);
        });
      } finally {
        this.classList.remove('kim-showcase--measuring');
      }
      if (height > 0) this.track.style.setProperty('--kim-showcase-card-height', `${Math.ceil(height)}px`);
      this.carousel.resize();
    }

    scheduleResize() {
      if (!this.carousel || this.resizeFrame) return;
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        if (!this.carousel) return;
        this.equalizeCards();
        this.updateControls();
      });
    }

    destroyCarousel() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = null;
      if (this.carousel) {
        this.carousel.off('select', this.onCarouselChange);
        this.carousel.off('resize', this.onCarouselChange);
        this.carousel.destroy();
        // Flickity appends its cells after non-cell children on destroy. Move the
        // mobile-only tail back after them so collection order stays unchanged.
        this.mobileExtraProducts.forEach(product => this.track.appendChild(product));
        this.carousel = null;
      }
      this.track.style.removeProperty('--kim-showcase-card-height');
      this.controls.hidden = true;
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.desktop?.removeEventListener('change', this.onBreakpoint);
      this.resizeObserver?.disconnect();
      this.destroyCarousel();
    }
  }
  customElements.define('kim-collection-showcase', KimCollectionShowcase);
})();

/* Kim Custom category cards: overflow past configured desktop columns uses theme Flickity; mobile masonry. */
(() => {
  if (customElements.get('kim-category-cards')) return;

  class KimCategoryCards extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.kim-categories__track');
      this.columns = Math.max(1, Number.parseInt(this.dataset.desktopColumns, 10) || 5);
      this.items = Array.from(this.track.querySelectorAll('.kim-categories__item'));
      this.controls = this.querySelector('.kim-categories__controls');
      this.previousButton = this.querySelector('.kim-categories__previous');
      this.nextButton = this.querySelector('.kim-categories__next');
      this.desktop = window.matchMedia('(min-width: 1068px)');
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.onBreakpoint = () => this.updateMode();
      this.desktop.addEventListener('change', this.onBreakpoint);
      this.previousButton.addEventListener('click', () => this.carousel?.previous(), options);
      this.nextButton.addEventListener('click', () => this.carousel?.next(), options);
      this.addEventListener('lazyloaded', () => this.scheduleRefresh(), options);
      this.addEventListener('load', () => this.scheduleRefresh(), { ...options, capture: true });
      this.addEventListener('shopify:block:select', event => {
        if (!this.carousel) return;
        const item = event.target.closest('.kim-categories__item');
        const index = this.items.indexOf(item);
        if (index >= 0) this.carousel.selectCell(index);
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.scheduleRefresh());
      this.resizeObserver.observe(this.track);
      this.updateMode();
      document.fonts?.ready.then(() => { if (this.isConnected) this.scheduleRefresh(); });
    }

    updateMode() {
      if (!this.desktop.matches) {
        this.destroyCarousel();
        return;
      }
      if (this.items.length > this.columns && !this.carousel && typeof window.Flickity === 'function') {
        this.carousel = new window.Flickity(this.track, {
          cellSelector: '.kim-categories__item',
          cellAlign: document.dir === 'rtl' ? 'right' : 'left',
          rightToLeft: document.dir === 'rtl',
          contain: true,
          groupCells: true,
          wrapAround: false,
          autoPlay: false,
          pageDots: false,
          prevNextButtons: false,
          accessibility: true
        });
        this.onCarouselChange = () => this.updateControls();
        this.carousel.on('select', this.onCarouselChange);
        this.carousel.on('resize', this.onCarouselChange);
      }
      this.equalizeCards();
      this.updateControls();
    }

    equalizeCards() {
      if (!this.desktop.matches) return;
      this.classList.add('kim-categories--measuring');
      let height = 0;
      try {
        this.items.forEach(item => { height = Math.max(height, item.getBoundingClientRect().height); });
      } finally {
        this.classList.remove('kim-categories--measuring');
      }
      if (height > 0) this.track.style.setProperty('--kim-category-card-height', `${Math.ceil(height)}px`);
      this.carousel?.resize();
    }

    updateControls() {
      const count = this.carousel?.slides.length || 0;
      this.controls.hidden = count <= 1;
      if (!this.carousel) return;
      this.previousButton.disabled = this.carousel.selectedIndex <= 0;
      this.nextButton.disabled = this.carousel.selectedIndex >= count - 1;
    }

    scheduleRefresh() {
      if (!this.desktop.matches || this.refreshFrame) return;
      this.refreshFrame = requestAnimationFrame(() => {
        this.refreshFrame = null;
        if (!this.isConnected || !this.desktop.matches) return;
        this.equalizeCards();
        this.updateControls();
      });
    }

    destroyCarousel() {
      if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = null;
      if (this.carousel) {
        this.carousel.off('select', this.onCarouselChange);
        this.carousel.off('resize', this.onCarouselChange);
        this.carousel.destroy();
        this.carousel = null;
      }
      this.track.style.removeProperty('--kim-category-card-height');
      this.controls.hidden = true;
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.desktop?.removeEventListener('change', this.onBreakpoint);
      this.resizeObserver?.disconnect();
      this.destroyCarousel();
    }
  }
  customElements.define('kim-category-cards', KimCategoryCards);
})();

/* Kim Custom image collage: resize grid units and reuse Habitat OverlayManager. */
(() => {
  if (customElements.get('kim-image-collage')) return;

  class KimImageCollage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('.kim-collage__grid');
      this.lightbox = this.querySelector('.kim-collage__lightbox');
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      if (this.lightbox) {
        this.preview = this.lightbox.querySelector('img');
        document.body.appendChild(this.lightbox);
        this.lightbox.addEventListener('click', event => {
          if (event.target === this.lightbox || event.target.closest('.kim-collage__lightbox-close')) this.closePreview();
        }, options);
        this.lightbox.addEventListener('keydown', event => {
          if (event.key === 'Escape') { event.preventDefault(); this.closePreview(); }
        }, options);
      }
      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-kim-collage-zoom]');
        if (!trigger || !this.contains(trigger) || !this.lightbox || typeof OverlayManager === 'undefined') return;
        event.preventDefault();
        this.preview.width = Number(trigger.dataset.imageWidth) || 1;
        this.preview.height = Number(trigger.dataset.imageHeight) || 1;
        this.preview.alt = trigger.dataset.imageAlt || '';
        this.preview.src = trigger.href;
        OverlayManager.open(this.lightbox);
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.updateGridUnit());
      this.resizeObserver.observe(this.grid);
      this.updateGridUnit();
    }

    updateGridUnit() {
      if (!this.grid.clientWidth) return;
      const gap = Number.parseFloat(getComputedStyle(this.grid).columnGap) || 0;
      const unit = `${Math.max(1, (this.grid.clientWidth - 11 * gap) / 12)}px`;
      if (this.grid.style.getPropertyValue('--kim-collage-unit') !== unit) this.grid.style.setProperty('--kim-collage-unit', unit);
    }

    closePreview() {
      if (!this.lightbox || typeof OverlayManager === 'undefined' || !OverlayManager.isOpen(this.lightbox)) return;
      OverlayManager.close(this.lightbox);
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.resizeObserver?.disconnect();
      this.closePreview();
      // Restore the portalled dialog to its owner so removal/reconnection has no
      // orphan modal, duplicate IDs, leaked focus handlers or body scroll lock.
      if (this.lightbox) this.appendChild(this.lightbox);
    }
  }
  customElements.define('kim-image-collage', KimImageCollage);
})();

/* Kim Custom video products: reuse QuickView rendering, Product-Drawer and OverlayManager. */
(() => {
  if (!customElements.get('kim-video-products')) {
    class KimVideoProducts extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.kim-videos__grid');
        this.desktop = window.matchMedia('(min-width: 768px)');
        this.onBreakpoint = () => this.updateMode();
        this.desktop.addEventListener('change', this.onBreakpoint);
        this.listeners = new AbortController();
        this.addEventListener('shopify:block:select', event => {
          const card = event.target.closest('.kim-videos__card');
          if (card && this.carousel) this.carousel.selectCell(card);
        }, { signal: this.listeners.signal });
        this.updateMode();
      }
      updateMode() {
        this.destroyCarousel();
        const columns = Number(this.desktop.matches ? this.dataset.desktopColumns : this.dataset.mobileColumns) || 2;
        const enabled = (this.desktop.matches ? this.dataset.scrollDesktop : this.dataset.scrollMobile) === 'true';
        const count = this.track.querySelectorAll('.kim-videos__card').length;
        if (!enabled || count <= columns || typeof window.Flickity !== 'function') return;
        this.style.setProperty('--kim-videos-active-columns', columns);
        this.track.classList.add('is-carousel');
        this.carousel = new window.Flickity(this.track, {
          cellSelector: '.kim-videos__card', cellAlign: document.dir === 'rtl' ? 'right' : 'left',
          rightToLeft: document.dir === 'rtl', contain: true, groupCells: true,
          wrapAround: false, prevNextButtons: true, pageDots: false, draggable: true, accessibility: true
        });
      }
      destroyCarousel() {
        this.carousel?.destroy(); this.carousel = null;
        this.track?.classList.remove('is-carousel');
        this.style.removeProperty('--kim-videos-active-columns');
      }
      disconnectedCallback() {
        this.desktop?.removeEventListener('change', this.onBreakpoint);
        this.listeners?.abort(); this.destroyCarousel();
      }
    }
    customElements.define('kim-video-products', KimVideoProducts);
  }

  const productCache = new Map();
  let videoOwner = null;
  let youtubePromise;
  const loadYouTube = () => {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (youtubePromise) return youtubePromise;
    youtubePromise = new Promise((resolve, reject) => {
      let checks = 0;
      const timer = setInterval(() => {
        if (window.YT?.Player) { clearInterval(timer); resolve(window.YT); }
        else if (++checks >= 150) { clearInterval(timer); reject(new Error('YouTube player unavailable')); }
      }, 100);
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.addEventListener('error', () => { clearInterval(timer); reject(new Error('YouTube API failed')); }, { once: true });
        document.head.appendChild(script);
      }
    }).catch(error => { youtubePromise = null; throw error; });
    return youtubePromise;
  };

  customElements.whenDefined('quick-view').then(() => {
    if (customElements.get('kim-video-quick-view')) return;
    const QuickView = customElements.get('quick-view');
    class KimVideoQuickView extends QuickView {
      connectedCallback() {
        this.drawer = document.getElementById('Product-Drawer');
        this.listeners = new AbortController();
        const options = { signal: this.listeners.signal };
        this.mobile = window.matchMedia('(max-width: 767px)');
        this.openLink = this.querySelector('.kim-videos__open');
        this.desktopLabel = this.openLink.getAttribute('aria-label');
        this.onMobileChange = () => {
          if (this.mobile.matches) {
            this.openLink.removeAttribute('aria-haspopup');
            this.openLink.setAttribute('aria-label', this.dataset.productTitle || this.dataset.videoTitle);
            if (videoOwner === this) OverlayManager.close(this.drawer);
          } else {
            this.openLink.setAttribute('aria-haspopup', 'dialog');
            if (this.desktopLabel !== null) this.openLink.setAttribute('aria-label', this.desktopLabel);
          }
        };
        this.mobile.addEventListener('change', this.onMobileChange);
        this.onMobileChange();
        this.addEventListener('click', event => this.handleClick(event), options);
        document.addEventListener('panel:closed', event => {
          if (event.detail.panel === this.drawer && videoOwner === this) this.cleanupVideo();
        }, options);
        document.addEventListener('quick-view:open', () => {
          // A normal Quick View can replace this shared drawer; remove only video decorations.
          if (videoOwner === this && !this.renderingNative) this.cleanupVideo();
        }, options);
      }

      async handleClick(event) {
        // On phones keep the real product link; do not fetch or mount video/Quick View.
        if (this.mobile.matches || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        if (!this.drawer || !this.dataset.productHandle || !this.dataset.videoId || typeof OverlayManager === 'undefined') return;
        event.preventDefault();
        if (this.getAttribute('aria-busy') === 'true') return;
        if (videoOwner) OverlayManager.close(videoOwner.drawer);
        if (OverlayManager.isOpen(this.drawer)) OverlayManager.close(this.drawer);
        QuickView.activeController?.abort();
        this.request = new AbortController();
        QuickView.activeController = this.request;
        const signal = this.request.signal;
        this.setAttribute('aria-busy', 'true');
        const root = theme.routes.root_url.replace(/\/+$/, '');
        const href = `${root}/products/${encodeURIComponent(this.dataset.productHandle)}?view=quick-view`;
        this.prepareDialog();
        try {
          let html = productCache.get(href);
          if (!html) {
            const response = await fetch(href, { signal });
            if (!response.ok) throw new Error('Quick View fetch failed');
            const parsed = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('#Product-Drawer-Content');
            if (!parsed) throw new Error('Quick View content missing');
            html = parsed.innerHTML;
            productCache.set(href, html);
          }
          if (signal.aborted || !this.isConnected || videoOwner !== this) return;
          this.renderingNative = true;
          const restoreCloseFocus = this.header.contains(document.activeElement);
          try { super.renderQuickView(html, href, this.dataset.productHandle); }
          finally { this.moveHeaderIntoContent(); this.renderingNative = false; }
          if (restoreCloseFocus) this.closeControl.focus();
        } catch (error) {
          if (error.name !== 'AbortError' && videoOwner === this) {
            const content = this.drawer.querySelector('#Product-Drawer-Content');
            content.replaceChildren();
            const message = document.createElement('p');
            message.textContent = '商品信息暂时无法加载，请重试或进入商品页面。';
            const link = document.createElement('a');
            link.className = 'button'; link.href = this.querySelector('a').href; link.textContent = '查看商品';
            content.append(message, link);
            this.moveHeaderIntoContent();
          }
        } finally {
          if (this.request?.signal === signal) this.removeAttribute('aria-busy');
        }
      }

      prepareDialog() {
        videoOwner = this;
        this.modalListeners = new AbortController();
        const options = { signal: this.modalListeners.signal };
        this.originalAttributes = new Map(['role', 'aria-modal', 'aria-label'].map(name => [name, this.drawer.getAttribute(name)]));
        this.drawer.setAttribute('role', 'dialog');
        this.drawer.setAttribute('aria-modal', 'true');
        this.drawer.setAttribute('aria-label', this.dataset.videoTitle);
        this.drawer.classList.add('kim-video-dialog');
        this.header = this.drawer.querySelector('.side-panel-header');
        this.headerParent = this.header.parentNode;
        this.headerNextSibling = this.header.nextSibling;
        this.closeControl = this.drawer.querySelector('.side-panel-close');
        this.closeAttributes = new Map(['tabindex', 'role'].map(name => [name, this.closeControl.getAttribute(name)]));
        this.closeControl.setAttribute('tabindex', '0');
        this.closeControl.setAttribute('role', 'button');
        this.closeControl.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); OverlayManager.close(this.drawer); }
        }, options);
        this.previousClose = OverlayManager.beforeCloseHandlers.get(this.drawer);
        this.onBeforeClose = proceed => { this.drawer.querySelector('#Product-Drawer-Content').replaceChildren(); proceed(); };
        OverlayManager.registerBeforeClose(this.drawer, this.onBeforeClose);
        const content = this.drawer.querySelector('#Product-Drawer-Content');
        const message = document.createElement('p'); message.textContent = '正在加载商品…';
        content.replaceChildren(message);
        this.moveHeaderIntoContent();
        this.icons = this.closest('kim-video-products').querySelector('.kim-videos__icons').content;
        this.video = document.createElement('div'); this.video.className = 'kim-video-dialog__video';
        const toolbar = document.createElement('div'); toolbar.className = 'kim-video-dialog__toolbar';
        this.playButton = document.createElement('button'); this.playButton.type = 'button'; this.setPlayState(false); this.playButton.disabled = true;
        this.playButton.addEventListener('click', () => {
          if (!this.player) return;
          if (this.player.getPlayerState() === 1) this.player.pauseVideo(); else this.player.playVideo();
        }, options);
        const share = document.createElement('button'); share.type = 'button'; share.setAttribute('aria-label', '分享视频'); share.title = '分享视频'; share.appendChild(this.icons.querySelector('[data-icon="share"]').cloneNode(true));
        share.addEventListener('click', () => this.shareVideo(), options);
        const watch = document.createElement('a'); watch.href = `https://www.youtube.com/watch?v=${encodeURIComponent(this.dataset.videoId)}`;
        watch.target = '_blank'; watch.rel = 'noopener'; watch.setAttribute('aria-label', '在 YouTube 查看'); watch.title = '在 YouTube 查看'; watch.appendChild(this.icons.querySelector('[data-icon="external"]').cloneNode(true));
        toolbar.append(this.playButton, share, watch);
        this.status = document.createElement('p'); this.status.className = 'kim-video-dialog__status'; this.status.setAttribute('role', 'status');
        const playerArea = document.createElement('div'); playerArea.className = 'kim-video-dialog__player';
        const iframe = document.createElement('iframe');
        iframe.id = `${this.id}-Player`; iframe.title = this.dataset.videoTitle;
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen'; iframe.allowFullscreen = true;
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        const params = new URLSearchParams({ enablejsapi: '1', origin: location.origin, playsinline: '1', controls: '1', rel: '0' });
        iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(this.dataset.videoId)}?${params}`;
        playerArea.appendChild(iframe); this.video.append(toolbar, this.status, playerArea);
        content.before(this.video);
        OverlayManager.open(this.drawer);
        loadYouTube().then(YT => {
          if (videoOwner !== this || !iframe.isConnected) return;
          this.player = new YT.Player(iframe, { events: {
            onReady: event => {
              if (videoOwner !== this || !iframe.isConnected) return;
              this.playButton.disabled = false;
              if (this.dataset.autoplay === 'true') event.target.playVideo();
            },
            onStateChange: event => {
              if (videoOwner === this) this.setPlayState(event.data === 1);
            },
            onError: () => {
              if (videoOwner === this) { this.playButton.disabled = true; this.status.textContent = '视频暂时无法播放，可通过 YouTube 链接查看。'; }
            }
          } });
        }).catch(() => {
          if (videoOwner === this) this.status.textContent = '请使用播放器内的播放按钮，或通过 YouTube 链接查看。';
        });
      }

      moveHeaderIntoContent() {
        const content = this.drawer.querySelector('#Product-Drawer-Content');
        content.prepend(this.header);
      }

      setPlayState(playing) {
        const label = playing ? '暂停视频' : '播放视频';
        this.playButton.setAttribute('aria-label', label); this.playButton.title = label;
        this.playButton.replaceChildren(this.icons.querySelector(`[data-icon="${playing ? 'pause' : 'play'}"]`).cloneNode(true));
      }

      async shareVideo() {
        const url = `https://www.youtube.com/watch?v=${encodeURIComponent(this.dataset.videoId)}`;
        if (navigator.share) {
          try { await navigator.share({ title: this.dataset.videoTitle, url }); return; }
          catch (error) { if (error.name === 'AbortError') return; }
        }
        try { await navigator.clipboard.writeText(url); if (videoOwner === this) this.status.textContent = '视频链接已复制'; }
        catch (_) {
          if (videoOwner !== this) return;
          let input = this.video.querySelector('input');
          if (!input) { input = document.createElement('input'); input.className = 'kim-video-dialog__share-url'; input.readOnly = true; input.setAttribute('aria-label', '视频分享链接'); this.status.after(input); }
          input.value = url; input.focus(); input.select(); this.status.textContent = '请复制视频链接';
        }
      }

      cleanupVideo() {
        this.request?.abort(); this.removeAttribute('aria-busy');
        this.modalListeners?.abort();
        this.player?.destroy(); this.player = null;
        this.video?.remove(); this.video = null;
        this.headerParent.insertBefore(this.header, this.headerNextSibling?.parentNode === this.headerParent ? this.headerNextSibling : null);
        this.drawer.classList.remove('kim-video-dialog');
        for (const [name, value] of this.originalAttributes || []) {
          if (value === null) this.drawer.removeAttribute(name); else this.drawer.setAttribute(name, value);
        }
        for (const [name, value] of this.closeAttributes || []) {
          if (value === null) this.closeControl.removeAttribute(name); else this.closeControl.setAttribute(name, value);
        }
        if (OverlayManager.beforeCloseHandlers.get(this.drawer) === this.onBeforeClose) {
          if (this.previousClose) OverlayManager.registerBeforeClose(this.drawer, this.previousClose);
          else OverlayManager.beforeCloseHandlers.delete(this.drawer);
        }
        if (videoOwner === this) videoOwner = null;
      }

      disconnectedCallback() {
        this.mobile?.removeEventListener('change', this.onMobileChange);
        this.request?.abort();
        if (videoOwner === this) {
          if (OverlayManager.isOpen(this.drawer)) OverlayManager.close(this.drawer);
          else this.cleanupVideo();
        }
        this.listeners?.abort();
      }
    }
    customElements.define('kim-video-quick-view', KimVideoQuickView);
  });
})();
